import { useTranslation } from 'react-i18next';
import { Li, Ul } from '@/common/components/DOM/DOM';
import { ModalLearning } from '@/common/components/ModalLearning/ModalLearning';
import { Translate } from '@/common/components/Translate/Translate';
import { useGetFlexibleBenefitsRolloverSummary } from '@/common/integrations/menhir/employee/flexible-benefits/flexible-benefits.hooks';
import { AccumulateBalanceActivityAsset } from './AccumulateBalanceActivityAsset';
import { FlexibleRetributionAsset } from './FlexibleRetributionAsset';
import { GrossSalaryActivityAsset } from './GrossSalaryActivityAsset';

export const ESWalletLearningCentre = ({
  onClose,
}: TESWalletLearningCentreProps) => {
  const { t } = useTranslation();
  const { rollover } = useGetFlexibleBenefitsRolloverSummary();
  const rolloverBenefits =
    rollover?.benefits
      .filter((benefit) => benefit.enabled)
      .map((benefit) => benefit.slug) ?? [];
  const hasRolloverEnabled = rolloverBenefits.length > 0;

  return (
    <ModalLearning.Root onClose={onClose}>
      <ModalLearning.Step
        title={t('learning_centre.es.wallet.home.steps.one.title')}
        description={
          <Translate i18nKey="learning_centre.es.wallet.home.steps.one.description" />
        }
        asset={
          <GrossSalaryActivityAsset
            cardTitle={t(
              'learning_centre.es.wallet.home.steps.one.illustration.card_title'
            )}
            cardValue={t(
              'learning_centre.es.wallet.home.steps.one.illustration.card_value'
            )}
            activityTitle={t(
              'learning_centre.es.wallet.home.steps.one.illustration.activity_title'
            )}
          />
        }
      />
      {hasRolloverEnabled ? (
        <ModalLearning.Step
          title={t('learning_centre.es.wallet.home.steps.two.title')}
          description={
            <Translate
              i18nKey="learning_centre.es.wallet.home.steps.two.description"
              components={{
                bullets: (
                  <Ul>
                    {rolloverBenefits.map((benefit, idx) => (
                      <Li key={idx}>
                        <Translate
                          i18nKey={`learning_centre.es.wallet.home.steps.two.spending.${benefit}`}
                        />
                      </Li>
                    ))}
                  </Ul>
                ),
              }}
            />
          }
          asset={
            <FlexibleRetributionAsset
              topBoxTitle={t(
                'learning_centre.es.wallet.home.steps.two.illustration.top_box_title'
              )}
              topBoxValue={t(
                'learning_centre.es.wallet.home.steps.two.illustration.top_box_value'
              )}
              bottomBoxTitle={t(
                'learning_centre.es.wallet.home.steps.two.illustration.bottom_box_title'
              )}
              bottomBoxDescription={t(
                'learning_centre.es.wallet.home.steps.two.illustration.bottom_box_description'
              )}
              bottomBoxValue={t(
                'learning_centre.es.wallet.home.steps.two.illustration.bottom_box_value'
              )}
            />
          }
        />
      ) : null}
      {hasRolloverEnabled ? (
        <ModalLearning.Step
          title={t('learning_centre.es.wallet.home.steps.three.title')}
          description={
            <Translate i18nKey="learning_centre.es.wallet.home.steps.three.description" />
          }
          asset={
            <AccumulateBalanceActivityAsset
              benefitTitle={t(
                'learning_centre.es.wallet.home.steps.three.illustration.benefit_title'
              )}
              cardTitle={t(
                'learning_centre.es.wallet.home.steps.three.illustration.card_title'
              )}
              cardValue={t(
                'learning_centre.es.wallet.home.steps.three.illustration.card_value'
              )}
              activityTitle={t(
                'learning_centre.es.wallet.home.steps.three.illustration.activity_title'
              )}
            />
          }
        />
      ) : null}
    </ModalLearning.Root>
  );
};

export type TESWalletLearningCentreProps = {
  onClose: () => void;
};
